import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X, Plus } from "lucide-react";
import api from "../lib/api";
import BusinessCard from "./BusinessCard";
import { getCategory } from "../lib/constants";

/**
 * Smart empty state shown when the search returns zero results.
 *
 *  - Restated message with the query + city in context
 *  - Up to 3 tappable "suggestion pills" to broaden the search
 *  - A horizontal scrollable inspiration row (4 random biz from a sane category)
 *  - A small CTA to add a missing business
 */
export default function EmptySearchState({
  query,
  category,
  city,
  onClearCategory,
  onClearCity,
  onResetAll,
}) {
  const [inspirations, setInspirations] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const sample = category || "restaurants";
        const { data } = await api.get("/businesses", {
          params: { category: sample, limit: 4, page: 1, sort: "rating" },
        });
        setInspirations(data.items || []);
      } catch (_e) {
        setInspirations([]);
      }
    })();
  }, [category]);

  const cat = category ? getCategory(category) : null;
  const cityLabel = city ? city.charAt(0).toUpperCase() + city.slice(1) : "";

  return (
    <div data-testid="empty-search-state" className="space-y-8">
      <div className="text-center py-10 bg-white rounded-2xl border border-stone-200">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-stone-100 text-stone-500 mb-4">
          <Search className="w-7 h-7" />
        </div>
        <h3 className="font-display text-2xl font-bold">
          Aucun résultat pour&nbsp;
          {query ? <span className="text-lokaso-red">« {query} »</span> : <span className="text-lokaso-red">cette recherche</span>}
          {cityLabel ? <> à <span className="text-lokaso-green">{cityLabel}</span></> : null}
        </h3>
        <p className="text-stone-600 mt-2">Essayez l'une de ces suggestions :</p>

        <div className="mt-5 flex flex-wrap gap-2 justify-center" data-testid="empty-pills">
          {category && (
            <button
              type="button"
              onClick={onClearCategory}
              data-testid="empty-pill-clear-category"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-lokaso-cream text-lokaso-dark text-sm font-semibold border border-stone-200 hover:border-lokaso-gold transition"
            >
              <Plus className="w-4 h-4" /> Voir tous les {cat?.name?.toLowerCase() || category}
            </button>
          )}
          {city && (
            <button
              type="button"
              onClick={onClearCity}
              data-testid="empty-pill-clear-city"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-lokaso-cream text-lokaso-dark text-sm font-semibold border border-stone-200 hover:border-lokaso-gold transition"
            >
              <Plus className="w-4 h-4" /> Chercher dans toute la Guinée
            </button>
          )}
          <button
            type="button"
            onClick={onResetAll}
            data-testid="empty-pill-reset"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-stone-700 text-sm font-semibold border border-stone-200 hover:bg-stone-50 transition"
          >
            <X className="w-4 h-4" /> Effacer la recherche
          </button>
        </div>
      </div>

      {inspirations.length > 0 && (
        <div data-testid="empty-inspirations">
          <h4 className="font-display text-lg font-bold mb-3">Vous pourriez aimer</h4>
          <div className="flex md:grid md:grid-cols-2 lg:grid-cols-4 gap-4 overflow-x-auto md:overflow-visible no-scrollbar -mx-4 px-4 md:mx-0 md:px-0">
            {inspirations.map((b) => (
              <div key={b.id} className="flex-shrink-0 w-[240px] md:w-auto">
                <BusinessCard business={b} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-center pb-4">
        <Link
          to="/business/register"
          className="text-sm text-stone-600 hover:text-lokaso-green underline"
          data-testid="empty-add-business-link"
        >
          Vous ne trouvez pas votre entreprise ? Ajoutez-la gratuitement →
        </Link>
      </div>
    </div>
  );
}
